import './senior-dcm-side-panel.scss';
import {GenericCollapsibleSidePanel} from '../generic-collapsible-side-panel/generic-collapsible-side-panel';
import {SeniorSidePanelComponents} from '../generic-collapsible-side-panel/senior-side-panel-components';
import {Option, SelectDropdown} from '../select-dropdown/select-dropdown';
import {ActivitiesSector} from '../../backend/interfaces/activities-sector';
import {useSeniorSidebar} from '../../contexts/seniors-sidebar/context';
import {useOffice} from '../../contexts/office/context';
import {Avatar, Box, Skeleton, Stack, SxProps, Theme, Fab} from '@mui/material';
import {Pallete} from '../../theme/bluebird';
import {useAuth} from '../../contexts/auth/context';
import eventEmitter from '../../events/event-emitter';
import CallIcon from '@mui/icons-material/Call';
import ProfileIcon from '@mui/icons-material/Description';
import {CREATE_VIDEOCALL} from '../../events/event-types';
import {CallContext} from '../../enums';
import {Tooltip} from '../tooltip/tooltip';
import {translations} from '../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {useSeniorProfilePdfExport} from '../../hooks/use-senior-profile-pdf-export';
import {VoximplantProcessState} from '../../contexts/office/types';

interface SeniorDcmSidePanelProps {
  onSeniorChange: (newSeniorId: string) => void;
  activitiesSectors?: ActivitiesSector[] | null;
}

export const SeniorDcmSidePanel = ({
  onSeniorChange,
  activitiesSectors,
}: SeniorDcmSidePanelProps) => {
  const {t} = useTranslation();
  const officeContext = useOffice();
  const seniorSidePanelContext = useSeniorSidebar();
  const authContext = useAuth();
  const {handleExportPdf} = useSeniorProfilePdfExport();
  const socialResponses = seniorSidePanelContext.state.senior?.socialResponses || [];
  const isVoximplantReadyForOutgoingCalls =
    officeContext.state.voximplant.connectionState === VoximplantProcessState.Done &&
    officeContext.state.voximplant.signedInState === VoximplantProcessState.Done &&
    !officeContext.state.voximplant.degraded;

  const dropdownSx: SxProps<Theme> = {
    width: '178px',
    height: '40px',
    mx: '11px',
    pl: '8px',
    backgroundColor: 'none',
    border: `1px solid ${Pallete.blue.lighter}`,
    textOverflow: 'ellipsis',
    '&:hover': {backgroundColor: Pallete.blue.lighter},
    '& .MuiSvgIcon-root': {
      color: Pallete.blue.base,
    },
  };

  return (
    <GenericCollapsibleSidePanel
      topContent={
        <Stack alignItems="center" gap="10px" className="senior-side-panel-top-container">
          {seniorSidePanelContext.state.senior === undefined ? (
            <>
              <Skeleton variant="circular" sx={{border: 'unset'}} />
              {!officeContext.state.sideNavigationDrawerCollapsed && (
                <Skeleton variant="rounded" sx={dropdownSx} />
              )}
            </>
          ) : (
            <>
              <Box className="avatar-container">
                <Avatar
                  className="avatar"
                  alt={seniorSidePanelContext.state.senior?.label}
                  sx={{
                    border:
                      socialResponses && socialResponses.length > 0
                        ? `2px solid ${socialResponses[0].foregroundColor}`
                        : '1px solid #b6b6b6',
                  }}
                  src={
                    seniorSidePanelContext.state.senior?.picture ||
                    seniorSidePanelContext.state.senior?.label.charAt(0)
                  }
                />
                {!officeContext.state.sideNavigationDrawerCollapsed && (
                  <Tooltip.If
                    condition={!isVoximplantReadyForOutgoingCalls}
                    title={t(translations.videocall.incomingCallConnecting)}
                    sx={{width: '40px'}}
                  >
                    <Fab
                      color="success"
                      aria-label="call"
                      className={
                        'call-button' + (!isVoximplantReadyForOutgoingCalls ? ' disabled' : '')
                      }
                      disabled={!seniorSidePanelContext.state.senior?.originalData.id}
                      onClick={() => {
                        if (!isVoximplantReadyForOutgoingCalls) return;
                        eventEmitter.emit(
                          CREATE_VIDEOCALL,
                          seniorSidePanelContext.state.senior?.originalData.id ?? '',
                          CallContext.Affiliation,
                          undefined,
                          undefined,
                          seniorSidePanelContext.state.senior?.originalData.displayName ||
                            seniorSidePanelContext.state.senior?.originalData.name
                        );
                      }}
                      size="medium"
                    >
                      <CallIcon />
                    </Fab>
                  </Tooltip.If>
                )}
                {!officeContext.state.sideNavigationDrawerCollapsed && (
                  <Tooltip title={t(translations.export.senior_profile.save_pdf)}>
                    <Fab
                      color="primary"
                      aria-label="export profile"
                      className="profile-export-button"
                      onClick={async () => {
                        const seniorId = seniorSidePanelContext.state.senior?.originalData.id;
                        const entity = authContext.state.session?.selectedAffiliation?.entity;
                        await handleExportPdf({seniorId, entity});
                      }}
                      size="medium"
                    >
                      <ProfileIcon />
                    </Fab>
                  </Tooltip>
                )}
              </Box>

              {!officeContext.state.sideNavigationDrawerCollapsed && (
                <SelectDropdown
                  key={seniorSidePanelContext.state.senior?.label}
                  options={
                    seniorSidePanelContext.state.list
                      ? Array.from(seniorSidePanelContext.state.list?.values()).filter(
                          (senior) => senior.originalData.isActive
                        )
                      : []
                  }
                  selectedOption={seniorSidePanelContext.state.senior || undefined}
                  onChange={(option) => onSeniorChange(option.key)}
                  isOptionEqualToValue={(option: Option, value: Option) =>
                    option.label === value.label
                  }
                  sx={dropdownSx}
                />
              )}
            </>
          )}
        </Stack>
      }
    >
      <SeniorSidePanelComponents
        showExpanded={!officeContext.state.sideNavigationDrawerCollapsed}
        activitiesSectors={activitiesSectors}
      />
    </GenericCollapsibleSidePanel>
  );
};
